// ============================================================
// Détail d'un commerce — Lot L04
//   - Infos générales (édition inline)
//   - Liste des boutiques + ajout
//   - Liste des utilisateurs + affectation boutique
//   - Création de l'admin initial (si aucun admin actif)
// ============================================================
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import superAdminApi from '../../services/superAdminApi';
import InputMontant from '../../components/common/InputMontant';
import {
  ArrowLeft, Building2, Store, Users, Plus, ShieldCheck,
  AlertCircle, KeyRound, MapPin, CreditCard, Calendar, Wallet,
  Boxes, Check, Pencil, Power, Send
} from 'lucide-react';
import toast from 'react-hot-toast';

function SectionTitre({ icon: Icon, children, count, action }) {
  return (
    <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
      <div className="flex items-center gap-2 text-slate-900">
        <Icon size={16} className="text-blue-600" />
        <h2 className="font-medium">{children}</h2>
        {count !== undefined && <span className="text-xs text-slate-400">({count})</span>}
      </div>
      {action}
    </div>
  );
}

function ModalAjouterBoutique({ commerceId, onClose, onSuccess }) {
  const [form, setForm] = useState({ nom: '', adresse: '', ville: '', telephone: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await superAdminApi.post(`/superadmin/commerces/${commerceId}/boutiques`, form);
      toast.success('Boutique ajoutée');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 space-y-3">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2"><Store size={16} className="text-blue-600" /> Nouvelle boutique</h3>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Nom <span className="text-red-600">*</span></label>
          <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required autoFocus
            className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Ville</label>
            <input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Téléphone</label>
            <input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Adresse</label>
          <input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })}
            className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
        </div>
        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? '…' : 'Ajouter'}</button>
        </div>
      </form>
    </div>
  );
}

function ModalAdminInitial({ commerceId, onClose, onSuccess }) {
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', mot_de_passe_temporaire: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await superAdminApi.post(`/superadmin/commerces/${commerceId}/admin-initial`, form);
      toast.success('Admin initial créé');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 space-y-3">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2"><ShieldCheck size={16} className="text-blue-600" /> Admin initial du commerce</h3>
        <p className="text-xs text-slate-400">Ce compte aura le rôle « Admin du commerce » et devra changer son mot de passe à la première connexion.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Prénom <span className="text-red-600">*</span></label>
            <input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} required autoFocus
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Nom <span className="text-red-600">*</span></label>
            <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Email <span className="text-red-600">*</span></label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
            placeholder="email@gmail.com"
            className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Mot de passe temporaire <span className="text-red-600">*</span></label>
          <input value={form.mot_de_passe_temporaire} onChange={(e) => setForm({ ...form, mot_de_passe_temporaire: e.target.value })} required minLength={8}
            placeholder="≥ 8 caractères"
            className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          <p className="text-xs text-slate-400 mt-1">Communique-le à l'admin par un canal sûr. Il devra le changer à sa première connexion.</p>
        </div>
        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? '…' : 'Créer'}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Modal : modifier les infos du commerce ────────────────────────
function ModalModifierCommerce({ commerce, onClose, onSuccess }) {
  const [form, setForm] = useState({
    nom: commerce.nom || '',
    telephone: commerce.telephone || '',
    email: commerce.email || '',
    adresse: commerce.adresse || '',
    ville: commerce.ville || '',
    pays: commerce.pays || '',
    devise: commerce.devise || 'GNF',
    langue: commerce.langue || 'fr',
    type_activite: commerce.type_activite || '',
    couleur_principale: commerce.couleur_principale || '#1e3a8a',
    couleur_secondaire: commerce.couleur_secondaire || '#64748b',
    max_boutiques: commerce.max_boutiques || 1,
    notes: commerce.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = 'w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || form.nom.trim().length < 2) {
      return setError('Le nom est requis (≥ 2 caractères)');
    }
    const maxB = parseInt(form.max_boutiques, 10);
    if (!maxB || maxB < 1) {
      return setError('Le nombre max de boutiques doit être ≥ 1');
    }
    setError('');
    setSaving(true);
    try {
      await superAdminApi.put(`/superadmin/commerces/${commerce.id}`, {
        ...form,
        nom: form.nom.trim(),
        max_boutiques: maxB,
      });
      toast.success('Commerce modifié');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg p-6 space-y-3 my-8">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2">
          <Pencil size={16} className="text-blue-600" /> Modifier le commerce
        </h3>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Nom <span className="text-red-600">*</span></label>
          <input value={form.nom} onChange={set('nom')} required autoFocus className={inputCls} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Téléphone</label>
            <input value={form.telephone} onChange={set('telephone')} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Email</label>
            <input type="email" value={form.email} onChange={set('email')} placeholder="email@gmail.com" className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Adresse</label>
          <input value={form.adresse} onChange={set('adresse')} className={inputCls} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Ville</label>
            <input value={form.ville} onChange={set('ville')} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Pays</label>
            <input value={form.pays} onChange={set('pays')} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Devise</label>
            <select value={form.devise} onChange={set('devise')} className={inputCls}>
              <option value="GNF">GNF</option>
              <option value="XOF">XOF</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Langue</label>
            <select value={form.langue} onChange={set('langue')} className={inputCls}>
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Max boutiques</label>
            <input type="number" min="1" value={form.max_boutiques} onChange={set('max_boutiques')} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Type d'activité</label>
            <input value={form.type_activite} onChange={set('type_activite')} placeholder="ex : valises et sacs" className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Couleur 1</label>
              <input type="color" value={form.couleur_principale} onChange={set('couleur_principale')}
                className="w-full h-9 bg-slate-100 border border-slate-300 rounded-lg cursor-pointer" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Couleur 2</label>
              <input type="color" value={form.couleur_secondaire} onChange={set('couleur_secondaire')}
                className="w-full h-9 bg-slate-100 border border-slate-300 rounded-lg cursor-pointer" />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Notes internes</label>
          <textarea rows={2} value={form.notes} onChange={set('notes')} className={inputCls} />
        </div>
        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? '…' : 'Enregistrer'}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Modal : modifier une boutique ─────────────────────────────────
function ModalModifierBoutique({ boutique, onClose, onSuccess }) {
  const [form, setForm] = useState({
    nom: boutique.nom || '',
    ville: boutique.ville || '',
    telephone: boutique.telephone || '',
    adresse: boutique.adresse || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputCls = 'w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || form.nom.trim().length < 2) {
      return setError('Le nom est requis (≥ 2 caractères)');
    }
    setError('');
    setSaving(true);
    try {
      await superAdminApi.put(`/superadmin/boutiques/${boutique.id}`, {
        ...form, nom: form.nom.trim(),
      });
      toast.success('Boutique modifiée');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 space-y-3">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2"><Pencil size={16} className="text-blue-600" /> Modifier la boutique</h3>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Nom <span className="text-red-600">*</span></label>
          <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required autoFocus className={inputCls} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Ville</label>
            <input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Téléphone</label>
            <input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Adresse</label>
          <input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} className={inputCls} />
        </div>
        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? '…' : 'Enregistrer'}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Modal : modifier un utilisateur ───────────────────────────────
const ROLE_LABELS = {
  admin: 'Admin du commerce',
  proprietaire: 'Propriétaire',
  gerant_vendeur: 'Gérant / vendeur',
  vendeur: 'Vendeur terrain',
};

function ModalModifierUser({ user, boutiques, onClose, onSuccess }) {
  const [form, setForm] = useState({
    prenom: user.prenom || '',
    nom: user.nom || '',
    email: user.email || '',
    role: user.role,
    actif: user.actif,
    boutique_id: user.boutique_id || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputCls = 'w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom.trim() || !form.nom.trim()) return setError('Prénom et nom requis');
    setError('');
    setSaving(true);
    try {
      await superAdminApi.put(`/superadmin/users/${user.id}`, {
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        email: form.email.trim(),
        role: form.role,
        actif: form.actif,
      });
      // Affectation boutique : seulement si elle a changé (trace l'historique)
      const ancienne = user.boutique_id || '';
      if (form.boutique_id !== ancienne) {
        await superAdminApi.post(`/superadmin/users/${user.id}/affecter-boutique`, {
          nouvelle_boutique_id: form.boutique_id || null,
          motif: 'Modification par le Super Admin',
        });
      }
      toast.success('Utilisateur modifié');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 space-y-3">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2"><Pencil size={16} className="text-blue-600" /> Modifier l'utilisateur</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Prénom <span className="text-red-600">*</span></label>
            <input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} required autoFocus className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Nom <span className="text-red-600">*</span></label>
            <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Email <span className="text-red-600">*</span></label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="email@gmail.com" className={inputCls} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Rôle</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
              {Object.entries(ROLE_LABELS).map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Boutique</label>
            <select value={form.boutique_id} onChange={(e) => setForm({ ...form, boutique_id: e.target.value })} className={inputCls}>
              <option value="">— Aucune (vue globale)</option>
              {boutiques.filter((b) => b.statut === 'actif').map((b) => (
                <option key={b.id} value={b.id}>{b.nom}</option>
              ))}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pt-1">
          <input type="checkbox" checked={form.actif}
            onChange={(e) => setForm({ ...form, actif: e.target.checked })} />
          Compte actif
        </label>
        {!form.actif && user.actif && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            L'utilisateur ne pourra plus se connecter tant que son compte est inactif.
          </p>
        )}
        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? '…' : 'Enregistrer'}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Modal : réinitialiser le mot de passe d'un user ───────────────
function ModalResetPassword({ user, onClose, onSuccess }) {
  const [mdp, setMdp] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mdp.length < 8) return setError('8 caractères minimum');
    setError('');
    setSaving(true);
    try {
      await superAdminApi.post(`/superadmin/users/${user.id}/reset-password`, {
        mot_de_passe_temporaire: mdp,
      });
      toast.success('Mot de passe réinitialisé');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 space-y-3">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2"><KeyRound size={16} className="text-blue-600" /> Réinitialiser le mot de passe</h3>
        <p className="text-xs text-slate-400">Pour <span className="text-slate-700 font-medium">{user.prenom} {user.nom}</span> ({user.email}). Il devra le changer à sa prochaine connexion.</p>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Mot de passe temporaire <span className="text-red-600">*</span></label>
          <input value={mdp} onChange={(e) => setMdp(e.target.value)} required minLength={8} autoFocus
            placeholder="≥ 8 caractères"
            className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          <p className="text-xs text-slate-400 mt-1">Communique-le à l'utilisateur par un canal sûr.</p>
        </div>
        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? '…' : 'Réinitialiser'}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Modal : changer / assigner un plan ────────────────────────────
function ModalChangerPlan({ commerceId, abonnementCourant, onClose, onSuccess }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(abonnementCourant?.plan_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    superAdminApi.get('/superadmin/plans')
      .then((r) => setPlans(r.data.filter((p) => p.actif)))
      .catch(() => setError('Impossible de charger les plans'));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!planId) return setError('Sélectionne un plan');
    setError(''); setSaving(true);
    try {
      await superAdminApi.post(`/superadmin/commerces/${commerceId}/abonnement`, { plan_id: planId });
      toast.success(abonnementCourant ? 'Plan changé' : 'Abonnement assigné');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 space-y-3">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2"><CreditCard size={16} className="text-blue-600" /> {abonnementCourant ? 'Changer le plan' : 'Assigner un plan'}</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {plans.map((p) => (
            <label key={p.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${planId === p.id ? 'border-blue-600 bg-blue-600/10' : 'border-slate-300 bg-slate-100 hover:border-slate-400'}`}>
              <input type="radio" name="plan" value={p.id}
                checked={planId === p.id} onChange={() => setPlanId(p.id)}
                className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-slate-900 text-sm font-medium">{p.nom} <span className="text-xs text-slate-400 font-mono ml-1">{p.code}</span></p>
                <p className="text-xs text-slate-500">{p.max_boutiques_default} boutique(s) · {p.max_utilisateurs_default} utilisateurs</p>
                <p className="text-xs text-slate-400">{new Intl.NumberFormat('fr-FR').format(p.prix_mensuel)} GNF/mois · {new Intl.NumberFormat('fr-FR').format(p.prix_annuel)} GNF/an</p>
              </div>
            </label>
          ))}
        </div>
        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? '…' : 'Confirmer'}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Modal : définir / prolonger la période d'essai ────────────────
function ModalEssai({ commerceId, abonnementCourant, onClose, onSuccess }) {
  const dansNJours = (n) => {
    const d = new Date(); d.setDate(d.getDate() + n);
    return d.toISOString().split('T')[0];
  };
  const expCourante = abonnementCourant?.date_expiration
    ? new Date(abonnementCourant.date_expiration).toISOString().split('T')[0]
    : null;
  const [dateFin, setDateFin] = useState(expCourante || dansNJours(30));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const estEssaiCourant = abonnementCourant?.plan_code === 'essai';

  const submit = async (e) => {
    e.preventDefault();
    if (!dateFin) return setError('Choisis une date de fin d\'essai');
    setError(''); setSaving(true);
    try {
      await superAdminApi.post(`/superadmin/commerces/${commerceId}/abonnement/essai`, { date_fin: dateFin });
      toast.success(`Essai actif jusqu'au ${new Date(dateFin).toLocaleDateString('fr-FR')}`);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2">
          <Calendar size={16} className="text-amber-600" />
          {estEssaiCourant ? 'Prolonger l\'essai' : 'Démarrer un essai gratuit'}
        </h3>
        <p className="text-xs text-slate-500">
          Définit la date de fin d'essai. Accès complet et gratuit jusqu'à cette date, puis lecture seule
          tant que le commerce ne passe pas en Premium. Aucun paiement n'est enregistré.
        </p>

        {/* Raccourcis de durée */}
        <div className="flex flex-wrap gap-2">
          {[14, 30, 60, 90].map((n) => (
            <button key={n} type="button" onClick={() => setDateFin(dansNJours(n))}
              className="px-3 py-1.5 rounded-lg text-xs border border-slate-300 bg-slate-100 hover:border-amber-500 hover:text-amber-700">
              {n} jours
            </button>
          ))}
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1">Fin de l'essai</label>
          <input type="date" value={dateFin} min={dansNJours(0)}
            onChange={(e) => setDateFin(e.target.value)}
            className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500" />
        </div>

        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? '…' : 'Confirmer'}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Modal : enregistrer un paiement ───────────────────────────────
function ModalPaiement({ abonnementId, devise, onClose, onSuccess }) {
  const [form, setForm] = useState({
    montant: '', methode: 'especes', duree_ajoutee_mois: 1,
    date_paiement: new Date().toISOString().split('T')[0], notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.montant || Number(form.montant) < 0) return setError('Montant invalide');
    setError(''); setSaving(true);
    try {
      const r = await superAdminApi.post(`/superadmin/abonnements/${abonnementId}/paiements`, {
        ...form,
        montant: parseInt(form.montant, 10),
        duree_ajoutee_mois: parseInt(form.duree_ajoutee_mois, 10),
      });
      toast.success(`Abonnement prolongé jusqu'au ${new Date(r.data.nouvelle_expiration).toLocaleDateString('fr-FR')}`);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 space-y-3">
        <h3 className="text-slate-900 font-semibold flex items-center gap-2"><Wallet size={16} className="text-blue-600" /> Enregistrer un paiement</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Montant ({devise || 'GNF'}) <span className="text-red-600">*</span></label>
            <InputMontant value={form.montant}
              onChange={(v) => setForm({ ...form, montant: v })}
              autoFocus
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Méthode <span className="text-red-600">*</span></label>
            <select value={form.methode} onChange={(e) => setForm({ ...form, methode: e.target.value })}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600">
              <option value="especes">Espèces</option>
              <option value="virement">Virement</option>
              <option value="orange_money">Orange Money</option>
              <option value="mtn_money">MTN Money</option>
              <option value="carte">Carte bancaire</option>
              <option value="autre">Autre</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Durée ajoutée (mois) <span className="text-red-600">*</span></label>
            <select value={form.duree_ajoutee_mois} onChange={(e) => setForm({ ...form, duree_ajoutee_mois: e.target.value })}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600">
              <option value={1}>1 mois</option>
              <option value={3}>3 mois</option>
              <option value={6}>6 mois</option>
              <option value={12}>12 mois</option>
              <option value={24}>24 mois</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Date du paiement</label>
            <input type="date" value={form.date_paiement}
              onChange={(e) => setForm({ ...form, date_paiement: e.target.value })}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Notes</label>
            <textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
        </div>
        {error && <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"><AlertCircle size={14} /> {error}</div>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">{saving ? '…' : 'Enregistrer'}</button>
        </div>
      </form>
    </div>
  );
}

const fmtMontant = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

// Modal : réinitialiser les données de test d'un commerce (irréversible)
function ModalReinitialiser({ commerce, onClose, onSuccess }) {
  const [saisie, setSaisie] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [saving, setSaving] = useState(false);
  const [resultat, setResultat] = useState(null);
  const [sauvegardeFaite, setSauvegardeFaite] = useState(false);
  const [sauvegardeEnCours, setSauvegardeEnCours] = useState(false);
  const nomOk = saisie.trim() === String(commerce.nom || '').trim();
  const ok = nomOk && motDePasse.length > 0;

  const sauvegarder = async () => {
    setSauvegardeEnCours(true);
    try {
      const { data } = await superAdminApi.post(
        `/superadmin/sauvegardes/${commerce.id}`,
        { notes: 'Sauvegarde avant réinitialisation des données' }
      );
      setSauvegardeFaite(true);
      const ko = data?.taille_octets ? ` (${Math.round(data.taille_octets / 1024)} Ko)` : '';
      toast.success(`Sauvegarde créée${ko}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec de la sauvegarde');
    } finally {
      setSauvegardeEnCours(false);
    }
  };

  const lancer = async () => {
    if (!ok) return;
    if (!sauvegardeFaite && !window.confirm(
      'Tu n\'as pas créé de sauvegarde dans cette fenêtre. Continuer SANS sauvegarde ?'
    )) return;
    setSaving(true);
    try {
      const { data } = await superAdminApi.post(
        `/superadmin/commerces/${commerce.id}/reinitialiser-donnees`,
        { confirmation: saisie.trim(), motDePasse }
      );
      setResultat(data);
      setMotDePasse('');
      toast.success('Données réinitialisées');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la réinitialisation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl p-5 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-red-700 flex items-center gap-2">
          <AlertCircle size={18} /> Réinitialiser les données de test
        </h2>
        {!resultat ? (
          <>
            <p className="text-sm text-slate-600 mt-2">
              Efface définitivement <strong>toutes les données de mouvement</strong> de
              « {commerce.nom} » : ventes, caisse, stock (remis à 0), coût moyen (remis à 0),
              approvisionnements, commandes, sorties vendeurs, dettes, clients, devis, retours,
              inventaires…
            </p>
            <p className="text-sm text-slate-600 mt-2">
              La <strong>configuration est conservée</strong> : produits, catégories, fournisseurs,
              utilisateurs, boutiques, prix, taxes, réglages et abonnement. La numérotation
              (bons, devis) repart à 1.
            </p>
            <div className="mt-3 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg p-3 text-sm">
              ⚠️ Action <strong>irréversible</strong>. N'affecte que ce commerce.
            </div>

            {/* Étape 1 : sauvegarde en un clic */}
            <button onClick={sauvegarder} disabled={sauvegardeEnCours || sauvegardeFaite}
              className={`mt-3 w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg font-semibold text-sm transition-colors ${
                sauvegardeFaite
                  ? 'bg-green-100 text-green-700 border border-green-300 cursor-default'
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white'
              }`}>
              {sauvegardeFaite ? '✓ Sauvegarde créée' : sauvegardeEnCours ? 'Sauvegarde en cours…' : '💾 1. Sauvegarder ce commerce maintenant'}
            </button>

            <label className="block text-sm font-medium text-slate-700 mt-4 mb-1">
              2. Pour confirmer, retape le nom exact du commerce :
            </label>
            <input value={saisie} onChange={e => setSaisie(e.target.value)} placeholder={commerce.nom}
              className="w-full bg-slate-100 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500" autoFocus />

            <label className="block text-sm font-medium text-slate-700 mt-4 mb-1">
              3. Saisis ton mot de passe Super Admin :
            </label>
            <input type="password" value={motDePasse} onChange={e => setMotDePasse(e.target.value)}
              placeholder="Mot de passe" autoComplete="current-password"
              className="w-full bg-slate-100 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500" />

            <div className="flex gap-2 mt-4">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold">Annuler</button>
              <button onClick={lancer} disabled={!ok || saving}
                className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-semibold">
                {saving ? 'Réinitialisation…' : 'Tout effacer'}
              </button>
            </div>
          </>
        ) : (
          <div className="mt-3">
            <p className="text-sm text-green-700 font-semibold">{resultat.message}</p>
            <div className="mt-2 max-h-60 overflow-y-auto text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-0.5">
              {Object.entries(resultat.resultat || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-mono text-slate-800">{String(v)}</span>
                </div>
              ))}
            </div>
            <button onClick={onSuccess} className="w-full mt-4 py-2 rounded-lg bg-slate-800 text-white font-semibold">Fermer</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CommerceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [abonnement, setAbonnement] = useState(null);
  const [paiements, setPaiements] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddBoutique, setShowAddBoutique] = useState(false);
  const [showModifier, setShowModifier] = useState(false);
  const [boutiqueEnEdition, setBoutiqueEnEdition] = useState(null);
  const [userEnEdition, setUserEnEdition] = useState(null);
  const [userResetMdp, setUserResetMdp] = useState(null);
  const [showAdminInitial, setShowAdminInitial] = useState(false);
  const [showChangerPlan, setShowChangerPlan] = useState(false);
  const [showPaiement, setShowPaiement] = useState(false);
  const [showEssai, setShowEssai] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const [modules, setModules] = useState({ catalogue: [], modules_actifs: [], tous_implicites: true });
  const [savingModules, setSavingModules] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      superAdminApi.get(`/superadmin/commerces/${id}`),
      superAdminApi.get(`/superadmin/commerces/${id}/abonnement`),
      superAdminApi.get(`/superadmin/commerces/${id}/modules`),
      superAdminApi.get(`/superadmin/commerces/${id}/demandes-abonnement`),
    ])
      .then(([dRes, aRes, mRes, demRes]) => {
        setData(dRes.data);
        setAbonnement(aRes.data.abonnement);
        setPaiements(aRes.data.paiements || []);
        setModules(mRes.data);
        setDemandes(demRes.data || []);
      })
      .catch((e) => toast.error(e.response?.data?.message || 'Erreur chargement'))
      .finally(() => setLoading(false));
  };

  const toggleModule = async (mod) => {
    if (mod.essentiel) return;  // un essentiel n'est jamais désactivable
    const courant = modules.catalogue.filter(m => m.actif).map(m => m.code);
    const codes = courant.includes(mod.code)
      ? courant.filter(c => c !== mod.code)
      : [...courant, mod.code];
    setSavingModules(true);
    try {
      const r = await superAdminApi.put(`/superadmin/commerces/${id}/modules`, { codes });
      // Recharger l'état complet pour refléter les implicites et les essentiels forcés
      const mRes = await superAdminApi.get(`/superadmin/commerces/${id}/modules`);
      setModules(mRes.data);
      toast.success(courant.includes(mod.code) ? `« ${mod.nom} » désactivé` : `« ${mod.nom} » activé`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    } finally {
      setSavingModules(false);
    }
  };

  const resoudreDemande = async (demandeId, statut) => {
    try {
      await superAdminApi.patch(`/superadmin/demandes-abonnement/${demandeId}`, { statut });
      toast.success(statut === 'traitee' ? 'Demande marquée comme traitée' : 'Demande rejetée');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (loading) return <p className="text-slate-400 text-sm">Chargement…</p>;
  if (!data) return <p className="text-red-600 text-sm">Commerce introuvable</p>;

  const { commerce, boutiques, users } = data;
  const hasActiveAdmin = users.some((u) => u.role === 'admin' && u.actif);

  const toggleStatutBoutique = async (b) => {
    const nouveau = b.statut === 'actif' ? 'inactif' : 'actif';
    if (!window.confirm(`${nouveau === 'inactif' ? 'Désactiver' : 'Réactiver'} la boutique « ${b.nom} » ?`)) return;
    try {
      await superAdminApi.patch(`/superadmin/boutiques/${b.id}/statut`, { statut: nouveau });
      toast.success(`Boutique ${nouveau === 'actif' ? 'réactivée' : 'désactivée'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/superadmin/commerces')} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm">
        <ArrowLeft size={14} /> Tous les commerces
      </button>

      <header className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${commerce.couleur_principale}1A`, border: `1px solid ${commerce.couleur_principale}40` }}>
          <Building2 size={20} style={{ color: commerce.couleur_principale }} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-slate-900 truncate">{commerce.nom}</h1>
          <p className="text-xs text-slate-400">{commerce.slug} · {commerce.devise} · max {commerce.max_boutiques} boutique(s) · statut {commerce.statut}</p>
          {commerce.adresse && <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin size={11} /> {commerce.adresse}{commerce.ville ? `, ${commerce.ville}` : ''}</p>}
        </div>
        <button onClick={() => setShowModifier(true)}
          className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-lg flex-shrink-0">
          <Pencil size={12} /> Modification Commerce
        </button>
      </header>

      {/* Abonnement */}
      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <SectionTitre icon={CreditCard} action={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowEssai(true)}
              className="flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1.5 rounded-lg">
              <Calendar size={12} /> {abonnement?.plan_code === 'essai' ? 'Prolonger l\'essai' : 'Démarrer un essai'}
            </button>
            {abonnement && abonnement.plan_code !== 'essai' && (
              <button onClick={() => setShowPaiement(true)}
                className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1.5 rounded-lg">
                <Wallet size={12} /> Enregistrer paiement
              </button>
            )}
            <button onClick={() => setShowChangerPlan(true)}
              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-lg">
              <CreditCard size={12} /> {abonnement ? 'Changer plan' : 'Assigner un plan'}
            </button>
          </div>
        }>
          Abonnement
        </SectionTitre>

        {/* Demandes en attente (envoyées par le commerce) */}
        {demandes.filter((d) => d.statut === 'en_attente').map((d) => (
          <div key={d.id} className="mb-3 bg-amber-50 border border-amber-300 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <Send size={15} className="text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-800">
                  {d.type === 'passage_premium' ? 'Demande de passage en Premium' : 'Demande de renouvellement'}
                  {d.periode ? ` · ${d.periode}` : ''}
                </p>
                <p className="text-xs text-amber-700/80">
                  {(d.demandeur_prenom || d.demandeur_nom) ? `Par ${d.demandeur_prenom || ''} ${d.demandeur_nom || ''} · ` : ''}
                  {new Date(d.created_at).toLocaleString('fr-FR')}
                </p>
                {d.message && <p className="text-xs text-slate-600 mt-1 italic">« {d.message} »</p>}
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button onClick={() => resoudreDemande(d.id, 'traitee')}
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded-lg">Marquer traitée</button>
                <button onClick={() => resoudreDemande(d.id, 'rejetee')}
                  className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1 rounded-lg">Rejeter</button>
              </div>
            </div>
          </div>
        ))}

        {!abonnement ? (
          <p className="text-sm text-slate-400 py-3 text-center">
            Aucun plan assigné à ce commerce.
            <br />
            <span className="text-xs">Assigne un plan, puis enregistre le premier paiement pour activer l'abonnement.</span>
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              <div className="bg-slate-100 rounded-lg px-3 py-2">
                <p className="text-xs text-slate-400">Plan</p>
                <p className="text-slate-900 text-sm font-medium flex items-center gap-1.5">
                  {abonnement.plan_nom}
                  {abonnement.plan_code === 'essai' && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">ESSAI</span>
                  )}
                </p>
                <p className="text-xs text-slate-400 font-mono">{abonnement.plan_code}</p>
              </div>
              <div className="bg-slate-100 rounded-lg px-3 py-2">
                <p className="text-xs text-slate-400 flex items-center gap-1"><Calendar size={11} /> Expire le</p>
                <p className="text-slate-900 text-sm font-medium">{fmtDate(abonnement.date_expiration)}</p>
                <p className="text-xs text-slate-400">
                  {abonnement.date_expiration
                    ? (() => {
                        const j = Math.round((new Date(abonnement.date_expiration) - new Date()) / 86400000);
                        if (j < 0) return <span className="text-red-600">expiré depuis {-j} j</span>;
                        if (j <= 15) return <span className="text-amber-700">dans {j} j</span>;
                        return `dans ${j} j`;
                      })()
                    : 'pas encore payé'}
                </p>
              </div>
              <div className="bg-slate-100 rounded-lg px-3 py-2">
                <p className="text-xs text-slate-400">Statut</p>
                <p className={`text-sm font-medium ${
                  abonnement.statut === 'actif' ? 'text-emerald-700' :
                  abonnement.statut === 'grace' ? 'text-amber-700' :
                  abonnement.statut === 'lecture_seule' ? 'text-red-600' : 'text-slate-500'}`}>
                  {abonnement.statut}
                </p>
              </div>
            </div>

            {paiements.length > 0 && (
              <div>
                <p className="text-xs text-slate-400 mb-2">Historique des paiements ({paiements.length})</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-200">
                        <th className="text-left px-2 py-1.5">Date</th>
                        <th className="text-right px-2 py-1.5">Montant</th>
                        <th className="text-left px-2 py-1.5">Méthode</th>
                        <th className="text-center px-2 py-1.5">Durée</th>
                        <th className="text-left px-2 py-1.5">Ancienne → Nouvelle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paiements.map((p) => (
                        <tr key={p.id} className="border-b border-slate-200/40">
                          <td className="px-2 py-1.5 text-slate-700">{fmtDate(p.date_paiement)}</td>
                          <td className="px-2 py-1.5 text-right text-emerald-700 font-mono">{fmtMontant(p.montant)} {p.devise}</td>
                          <td className="px-2 py-1.5 text-slate-500">{p.methode}</td>
                          <td className="px-2 py-1.5 text-center text-slate-500">{p.duree_ajoutee_mois} m</td>
                          <td className="px-2 py-1.5 text-slate-400">{fmtDate(p.ancienne_expiration)} → {fmtDate(p.nouvelle_expiration)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Modules */}
      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <SectionTitre icon={Boxes}>
          Modules activés
        </SectionTitre>
        {modules.tous_implicites && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            Configuration « tous modules actifs » (cas par défaut). Clique sur un module pour le désactiver — la liste explicite remplacera le défaut implicite.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {modules.catalogue.map((m) => (
            <button key={m.code}
              onClick={() => toggleModule(m)}
              disabled={m.essentiel || savingModules}
              className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${
                m.actif
                  ? 'bg-blue-600/10 border-blue-600/40 hover:bg-blue-50'
                  : 'bg-slate-100 border-slate-300 hover:border-slate-400 opacity-70'
              } ${m.essentiel ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-slate-900 text-sm font-medium flex items-center gap-2">
                    {m.nom}
                    {m.essentiel && <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">essentiel</span>}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">{m.description}</p>
                </div>
                {m.actif && <Check size={14} className="text-blue-600 flex-shrink-0 mt-1" />}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Boutiques */}
      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <SectionTitre icon={Store} count={boutiques.length} action={
          boutiques.filter(b => b.statut !== 'archive').length < commerce.max_boutiques && (
            <button onClick={() => setShowAddBoutique(true)}
              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-lg">
              <Plus size={12} /> Ajouter
            </button>
          )
        }>
          Boutiques
        </SectionTitre>
        {boutiques.length === 0 ? (
          <p className="text-sm text-slate-400 py-3 text-center">Aucune boutique. Ajoute-en une pour permettre l'affectation des gérants.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {boutiques.map((b) => (
              <div key={b.id} className="bg-slate-100 border border-slate-300 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-slate-900 text-sm font-medium truncate">{b.nom}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${b.statut === 'actif' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                    {b.statut}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  {b.ville ? <p className="text-xs text-slate-400">{b.ville}</p> : <span />}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setBoutiqueEnEdition(b)}
                      title="Modifier la boutique"
                      className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                      <Pencil size={11} /> Modification Boutique
                    </button>
                    {b.statut !== 'archive' && (
                      <button onClick={() => toggleStatutBoutique(b)}
                        title={b.statut === 'actif' ? 'Désactiver' : 'Réactiver'}
                        className={`flex items-center gap-1 text-xs hover:underline ${b.statut === 'actif' ? 'text-amber-700' : 'text-emerald-700'}`}>
                        <Power size={11} /> {b.statut === 'actif' ? 'Désactiver' : 'Réactiver'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Utilisateurs */}
      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <SectionTitre icon={Users} count={users.length} action={
          !hasActiveAdmin && (
            <button onClick={() => setShowAdminInitial(true)}
              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-lg">
              <KeyRound size={12} /> Créer admin initial
            </button>
          )
        }>
          Utilisateurs
        </SectionTitre>
        {users.length === 0 ? (
          <p className="text-sm text-slate-400 py-3 text-center">Aucun utilisateur. Commence par créer l'admin initial du commerce.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 border-b border-slate-200">
                  <th className="text-left px-2 py-2">Nom</th>
                  <th className="text-left px-2 py-2">Email</th>
                  <th className="text-left px-2 py-2">Rôle</th>
                  <th className="text-left px-2 py-2">Boutique</th>
                  <th className="text-left px-2 py-2">Statut</th>
                  <th className="text-left px-2 py-2">Actif</th>
                  <th className="text-right px-2 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-200/50">
                    <td className="px-2 py-2 text-slate-900">{u.prenom} {u.nom}</td>
                    <td className="px-2 py-2 text-slate-500">{u.email}</td>
                    <td className="px-2 py-2 text-slate-500">{u.role}</td>
                    <td className="px-2 py-2 text-slate-500">{u.boutique_nom || <span className="italic text-slate-400">— (vue globale)</span>}</td>
                    <td className="px-2 py-2">
                      {(() => {
                        const act = u.derniere_activite ? new Date(u.derniere_activite) : null;
                        const rel = (d) => {
                          const s = Math.floor((Date.now() - d.getTime()) / 1000);
                          if (s < 60) return "à l'instant";
                          if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
                          if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
                          return d.toLocaleDateString('fr-FR');
                        };
                        return (
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                            <span className={`w-2 h-2 rounded-full ${u.en_ligne ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                            <span className={u.en_ligne ? 'text-emerald-700' : 'text-slate-400'}>
                              {u.en_ligne ? 'En ligne' : act ? `Vu ${rel(act)}` : 'Jamais connecté'}
                            </span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2">{u.actif ? <span className="text-emerald-700">✓</span> : <span className="text-red-600">✗</span>}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setUserEnEdition(u)}
                        className="text-xs text-blue-600 hover:underline mr-3">
                        Modification Utilisateur
                      </button>
                      <button onClick={() => setUserResetMdp(u)}
                        className="text-xs text-amber-700 hover:underline mr-3">
                        Mot de passe
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Réinitialiser la double authentification (2FA) de ${u.prenom} ${u.nom} ?\n\nÀ utiliser s'il a perdu son téléphone. Il pourra ensuite se reconnecter avec son mot de passe seul.`)) return;
                          try {
                            await superAdminApi.post(`/superadmin/users/${u.id}/reset-2fa`);
                            toast.success('Double authentification réinitialisée');
                          } catch (e) {
                            toast.error(e.response?.data?.message || 'Erreur');
                          }
                        }}
                        className="text-xs text-purple-700 hover:underline">
                        2FA
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showModifier && (
        <ModalModifierCommerce commerce={commerce}
          onClose={() => setShowModifier(false)}
          onSuccess={() => { setShowModifier(false); load(); }} />
      )}
      {/* Zone de danger — réinitialisation des données de test */}
      <div className="mt-8 border-2 border-red-200 rounded-xl p-5 bg-red-50/60">
        <h3 className="text-sm font-bold text-red-700 flex items-center gap-2">
          <AlertCircle size={16} /> Zone de danger
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Avant la mise en production réelle, efface les données de test (ventes, caisse, stock,
          approvisionnements, commandes, dettes…). La configuration (produits, fournisseurs,
          utilisateurs, réglages) est conservée. Pense à faire une sauvegarde d'abord.
        </p>
        <button onClick={() => setShowReset(true)}
          className="mt-3 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg">
          <AlertCircle size={16} /> Réinitialiser les données de test
        </button>
      </div>

      {showReset && commerce && (
        <ModalReinitialiser commerce={commerce}
          onClose={() => setShowReset(false)}
          onSuccess={() => { setShowReset(false); load(); }} />
      )}

      {showAddBoutique && (
        <ModalAjouterBoutique commerceId={id} onClose={() => setShowAddBoutique(false)}
          onSuccess={() => { setShowAddBoutique(false); load(); }} />
      )}
      {boutiqueEnEdition && (
        <ModalModifierBoutique boutique={boutiqueEnEdition}
          onClose={() => setBoutiqueEnEdition(null)}
          onSuccess={() => { setBoutiqueEnEdition(null); load(); }} />
      )}
      {userEnEdition && (
        <ModalModifierUser user={userEnEdition} boutiques={boutiques}
          onClose={() => setUserEnEdition(null)}
          onSuccess={() => { setUserEnEdition(null); load(); }} />
      )}
      {userResetMdp && (
        <ModalResetPassword user={userResetMdp}
          onClose={() => setUserResetMdp(null)}
          onSuccess={() => setUserResetMdp(null)} />
      )}
      {showAdminInitial && (
        <ModalAdminInitial commerceId={id} onClose={() => setShowAdminInitial(false)}
          onSuccess={() => { setShowAdminInitial(false); load(); }} />
      )}
      {showChangerPlan && (
        <ModalChangerPlan commerceId={id} abonnementCourant={abonnement}
          onClose={() => setShowChangerPlan(false)}
          onSuccess={() => { setShowChangerPlan(false); load(); }} />
      )}
      {showPaiement && abonnement && (
        <ModalPaiement abonnementId={abonnement.id} devise={commerce.devise}
          onClose={() => setShowPaiement(false)}
          onSuccess={() => { setShowPaiement(false); load(); }} />
      )}
      {showEssai && (
        <ModalEssai commerceId={id} abonnementCourant={abonnement}
          onClose={() => setShowEssai(false)}
          onSuccess={() => { setShowEssai(false); load(); }} />
      )}
    </div>
  );
}
